/**
 * Resolve site geo context for tier-1 sentiment fetchers.
 */
import { auth, db } from '../../firebase';
import { resolveStateFromCoords } from '../dataProviders/regulatoryDb';
import type { SentimentFetchOptions } from './types';
import {
  raceWithAbort,
  rethrowIfAborted,
  throwIfAborted,
} from '../../lib/abortSignal';

export interface SentimentSiteContext {
  lat: number;
  lng: number;
  state: string | null;
  locality: string | null;
  county: string | null;
  fipsState: string | null;
  fipsCounty: string | null;
  fipsTract: string | null;
}

async function loadSiteDoc(
  siteId: string,
  signal?: AbortSignal,
): Promise<Record<string, unknown> | null> {
  throwIfAborted(signal);
  const user = auth.currentUser;
  if (!user || !siteId) return null;
  try {
    const { doc, getDoc } = await import('firebase/firestore');
    const snap = await raceWithAbort(
      getDoc(doc(db, 'users', user.uid, 'sites', siteId)),
      signal,
    );
    throwIfAborted(signal);
    if (!snap.exists()) return null;
    return snap.data() as Record<string, unknown>;
  } catch (error) {
    rethrowIfAborted(signal, error);
    return null;
  }
}

function parseLocality(address: string | undefined): string | null {
  if (!address) return null;
  const head = address.split(',')[0]?.trim();
  return head && head.length > 0 && head.length < 80 ? head : null;
}

/** Merge explicit opts with Firestore site doc + FCC census lookup. */
export async function resolveSentimentSiteContext(
  siteId: string,
  opts?: SentimentFetchOptions,
): Promise<SentimentSiteContext | null> {
  throwIfAborted(opts?.signal);
  const doc = opts?.explicitContext ? null : await loadSiteDoc(siteId, opts?.signal);
  const lat = opts?.lat ?? (typeof doc?.lat === 'number' ? doc.lat : undefined);
  const lng = opts?.lng ?? (typeof doc?.lng === 'number' ? doc.lng : undefined);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  const address = typeof doc?.address === 'string' ? doc.address : undefined;
  const locality = opts?.locality ?? parseLocality(address);
  let state = opts?.state ?? null;
  let county = opts?.county ?? null;
  let fipsState = opts?.fipsState ?? null;
  let fipsCounty = opts?.fipsCounty ?? null;
  let fipsTract = opts?.fipsTract ?? null;

  if (!state) {
    state = await resolveStateFromCoords(lat!, lng!, opts?.signal);
    throwIfAborted(opts?.signal);
  }

  if (!fipsState || !fipsCounty) {
    try {
      const { fetchWithTimeout } = await import('../dataProviders/types');
      const url = `https://geo.fcc.gov/api/census/block/find?format=json&latitude=${lat}&longitude=${lng}`;
      const res = await fetchWithTimeout(url, 12_000, opts?.signal);
      throwIfAborted(opts?.signal);
      if (res.ok) {
        const json = await res.json() as {
          County?: { name?: string; FIPS?: string };
          State?: { code?: string; FIPS?: string };
          Block?: { FIPS?: string };
        };
        if (json.State?.FIPS) fipsState = json.State.FIPS;
        if (json.State?.code && !state) state = json.State.code;
        if (json.County?.name) county = json.County.name;
        if (json.County?.FIPS && json.State?.FIPS) {
          fipsCounty = json.County.FIPS.slice(json.State.FIPS.length);
        }
        const blockFips = json.Block?.FIPS;
        if (blockFips && fipsState && fipsCounty && blockFips.length >= 11) {
          fipsTract = blockFips.slice(5, 11);
        }
      }
    } catch (error) {
      rethrowIfAborted(opts?.signal, error);
      /* FCC outage — proceed with partial context */
    }
  }

  return {
    lat: lat!,
    lng: lng!,
    state,
    locality,
    county,
    fipsState,
    fipsCounty,
    fipsTract,
  };
}

export async function hasResolvableSiteGeo(
  siteId: string,
  opts?: SentimentFetchOptions,
): Promise<boolean> {
  if (Number.isFinite(opts?.lat) && Number.isFinite(opts?.lng)) return true;
  const ctx = await resolveSentimentSiteContext(siteId, opts);
  return ctx != null;
}
